<script setup lang="ts">
import {onMounted, ref, watch} from "vue";

interface MedicineCabinetCategoryProductSuggestionFilter {
    slug: "stock" | "sale";
    name: string;
}

import {
    MedicineCabinetCategory,
    MedicineCabinetCategoryProduct
} from "@/core/interfaces/medicine-cabinet/MedicineCabinet";
import Product from "@/product/interfaces/Product";
import {medicineCabinetApi} from "@/container";
import {MetaData} from "@/core/interfaces/api";
import Icon from "@/core/components/icon/Icon.vue";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/core/components/ui/select";
import Loader from "@/core/components/base/spinner/Loader.vue";
import {AcceptableValue} from "reka-ui";

defineEmits<{ (e: 'productSelected', payload: number[]): void; }>();
const {category, selectedProducts} = defineProps<{ category: MedicineCabinetCategory, selectedProducts?: Product[] }>();
const filters: MedicineCabinetCategoryProductSuggestionFilter[] = [
    {slug: "stock", name: "Les plus en stock"},
    {slug: "sale", name: "Les plus vendus"}
];
const selectedFilter = ref<MedicineCabinetCategoryProductSuggestionFilter>(filters[0]);
const meta = ref<MetaData>();
const products = ref<MedicineCabinetCategoryProduct[]>();
const page = ref<number>(1);
const loading = ref<boolean>(false);

onMounted(() => {
    fetchProducts();
});

watch([page, selectedFilter], () => {
    fetchProducts();
});

watch(() => selectedProducts, () => {
    fetchProducts();
});

/**
 * Fetch products.
 */
const fetchProducts = (): Promise<void> => {
    loading.value = true;
    return medicineCabinetApi().products(category.id, selectedFilter.value.slug, page.value)
        .then(response => {
            meta.value = response.meta;
            const data = [...response.data];
            if (selectedProducts) {
                const mapFeatures = selectedProducts.map(product => product.id);
                products.value = data.filter(item => !mapFeatures.includes(item.product.id));
            } else {
                products.value = data;
            }
        })
        .finally(() => loading.value = false);
};

const handleFilterUpdate = (filterSlug: AcceptableValue) => {
    const newFilter = filters.find(filter => filter.slug === filterSlug);

    if (newFilter) {
        selectedFilter.value = newFilter;
    }
};

</script>

<template>
  <div v-if="meta?.total" class="p-4" :style="{backgroundColor: `${category.color}`}">
    <div class="flex flex-row justify-between items-center">
      <div class="flex flex-row gap-4">
        <h3>Nous vous suggérons d'ajouter les produits suivants</h3>
        <Loader v-if="loading" />
      </div>
      <Select :model-value="selectedFilter.slug" @update:model-value="handleFilterUpdate">
        <SelectTrigger class="w-48 bg-white">
          <SelectValue placeholder="Opérateur" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem v-for="filter in filters" :key="filter.slug" :value="filter.slug">
            {{ filter.name }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div v-if="!products?.length" class="pt-12">
      <p class="text-center">
        Tous les produits de cette page ont déjà été ajouté à la sélection.
      </p>
    </div>
    <div class="grid grid-cols-2 gap-4 pt-4">
      <div
        v-for="item in products"
        :key="item.id"
        class="bg-white flex rounded-lg overflow-hidden shadow"
      >
        <div class="flex flex-1 flex-row justify-between">
          <div class="flex flex-1 flex-col justify-between p-4">
            <div>
              <h3>{{ item.product.name }}</h3>
              <p>{{ item.product.id_13 }}</p>
            </div>
            <div class="flex flex-col align-end justify-end">
              <p>
                Stock <span class="font-semibold">{{ item.product.stock }}</span>
              </p>
              <p>
                Prix <span class="font-semibold">{{ item.product.price || "-" }}€</span>
              </p>
            </div>
          </div>
          <div
            class="flex w-24 flex-col items-center justify-center bg-gray-100 hover:opacity-75 cursor-pointer"
            @click="$emit('productSelected', [item.product.id])"
          >
            <icon name="mdi-plus" :size="40" :color="category.color" />
            <p>Ajouter</p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="meta?.total" class="text-center py-4">
      <v-pagination
        v-model="page"
        :length="meta.last_page"
        :total-visible="7"
        circle
      />
    </div>
  </div>
</template>
